import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { prestadorFetch, getPrestadorToken } from '../../contexts/PrestadorAuthContext';
import API_CONFIG from '../../config/api';
import { DOCS_NOTA_FISCAL, STATUS_NOTA_PORTAL } from '../../constants/prestadorDocumentos';

const formatMoeda = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

export default function PrestadorNotaMesDialog({ open, onClose, numeroContrato, mes, onSalvo }) {
  const [form, setForm] = useState({
    numeroNotaFiscal: '',
    dataEmissao: '',
    valorBruto: '',
    valorLiquido: ''
  });
  const [pdfNota, setPdfNota] = useState(null);
  const [docsExtras, setDocsExtras] = useState([]);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const competencia = mes?.competencia || '';
  const modoVisualizacao = Boolean(mes?.notaId && !mes?.podeEnviar);

  useEffect(() => {
    if (!open || !modoVisualizacao || !mes?.notaId) {
      setDetalhe(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setCarregandoDetalhe(true);
      setErro('');
      try {
        const res = await prestadorFetch(`/api/prestador/notas/${mes.notaId}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar nota');
        if (!cancelled) setDetalhe(data);
      } catch (e) {
        if (!cancelled) setErro(e.message);
      } finally {
        if (!cancelled) setCarregandoDetalhe(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, modoVisualizacao, mes?.notaId]);

  const abrirPdf = async (url) => {
    try {
      const token = getPrestadorToken();
      const res = await fetch(`${API_CONFIG.BACKEND_URL}${url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Não foi possível abrir o documento.');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErro(e.message);
    }
  };

  const adicionarDocExtra = () => {
    setDocsExtras((prev) => [...prev, { tipo: 'Relatório', file: null, key: Date.now() + Math.random() }]);
  };

  const handleSubmit = async () => {
    setErro('');
    if (!form.numeroNotaFiscal || !form.dataEmissao) {
      setErro('Informe número da NF e data de emissão.');
      return;
    }
    if (!pdfNota) {
      setErro('Anexe o PDF da nota fiscal.');
      return;
    }

    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('numeroNotaFiscal', form.numeroNotaFiscal);
      fd.append('dataEmissao', form.dataEmissao);
      fd.append('competencia', competencia);
      fd.append('valorBruto', form.valorBruto.replace(/\./g, '').replace(',', '.') || '');
      fd.append('valorLiquido', form.valorLiquido.replace(/\./g, '').replace(',', '.') || '');
      fd.append('pdfNotaFiscal', pdfNota);

      const tipos = [];
      docsExtras.forEach((d) => {
        if (d.file) {
          fd.append('documentos', d.file);
          tipos.push(d.tipo);
        }
      });
      fd.append('tiposDocumento', JSON.stringify(tipos));

      const res = await prestadorFetch(
        `/api/prestador/contratos/${encodeURIComponent(numeroContrato)}/notas`,
        { method: 'POST', body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar nota');

      onSalvo?.();
      onClose();
      setForm({ numeroNotaFiscal: '', dataEmissao: '', valorBruto: '', valorLiquido: '' });
      setPdfNota(null);
      setDocsExtras([]);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {modoVisualizacao ? 'Documentação enviada' : 'Enviar nota fiscal'} — competência {competencia}
        {mes?.status && (
          <Chip
            label={STATUS_NOTA_PORTAL[mes.status] || mes.status}
            size="small"
            sx={{ ml: 1 }}
          />
        )}
        {mes?.status === 'devolvida_prestador' && (
          <Chip label="Devolvida pelo setor de contratos" color="warning" size="small" sx={{ ml: 1 }} />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {mes?.observacaoDevolucao && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Motivo da devolução</Typography>
            {mes.observacaoDevolucao}
          </Alert>
        )}
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {modoVisualizacao ? (
          carregandoDetalhe ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detalhe ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Nº NF
                  </Typography>
                  <Typography variant="body2">{detalhe.numeroNotaFiscal || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Emissão
                  </Typography>
                  <Typography variant="body2">{detalhe.dataEmissao || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Bruto
                  </Typography>
                  <Typography variant="body2">{formatMoeda(detalhe.valorBruto)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Líquido
                  </Typography>
                  <Typography variant="body2">{formatMoeda(detalhe.valorLiquido)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Processo
                  </Typography>
                  <Typography variant="body2">{detalhe.numeroProcesso || '—'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Documentos
              </Typography>
              <List dense disablePadding>
                {detalhe.temPdfNota && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="Nota fiscal (PDF)" secondary="Documento principal" />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="primary"
                        title="Abrir PDF da nota"
                        onClick={() => abrirPdf(`/api/prestador/notas/${detalhe.id}/pdf`)}
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                {(detalhe.documentos || []).map((doc) => (
                  <ListItem key={doc.id} sx={{ px: 0 }}>
                    <ListItemText primary={doc.tipo || 'Documento'} secondary={doc.nome} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="primary"
                        title="Abrir documento"
                        onClick={() =>
                          abrirPdf(`/api/prestador/notas/${detalhe.id}/documentos/${doc.id}/view`)
                        }
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {!detalhe.temPdfNota && !(detalhe.documentos || []).length && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum documento anexado para esta competência.
                </Typography>
              )}
            </Box>
          ) : null
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Os dados serão encaminhados ao setor de contratos para aprovação e alimentarão o SGC
              automaticamente.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Número da nota fiscal"
                  fullWidth
                  size="small"
                  value={form.numeroNotaFiscal}
                  onChange={(e) => setForm((p) => ({ ...p, numeroNotaFiscal: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de emissão"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.dataEmissao}
                  onChange={(e) => setForm((p) => ({ ...p, dataEmissao: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor bruto (R$)"
                  fullWidth
                  size="small"
                  value={form.valorBruto}
                  onChange={(e) => setForm((p) => ({ ...p, valorBruto: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor líquido (R$)"
                  fullWidth
                  size="small"
                  value={form.valorLiquido}
                  onChange={(e) => setForm((p) => ({ ...p, valorLiquido: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
                  {pdfNota ? pdfNota.name : 'Anexar nota fiscal (PDF) *'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,application/pdf"
                    onChange={(e) => setPdfNota(e.target.files?.[0] || null)}
                  />
                </Button>
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Documentos complementares
            </Typography>
            <Stack spacing={1}>
              {docsExtras.map((d, idx) => (
                <Box key={d.key} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={d.tipo}
                      onChange={(e) =>
                        setDocsExtras((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, tipo: e.target.value } : x))
                        )
                      }
                    >
                      {DOCS_NOTA_FISCAL.filter((t) => t.value !== 'Nota Fiscal').map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                    {d.file ? d.file.name : 'Arquivo'}
                    <input
                      type="file"
                      hidden
                      onChange={(e) =>
                        setDocsExtras((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, file: e.target.files?.[0] || null } : x))
                        )
                      }
                    />
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setDocsExtras((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Remover
                  </Button>
                </Box>
              ))}
              <Button size="small" onClick={adicionarDocExtra} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                + Adicionar documento
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        {!modoVisualizacao && (
          <Button variant="contained" onClick={handleSubmit} disabled={enviando} sx={{ textTransform: 'none' }}>
            {enviando ? 'Enviando...' : 'Enviar para aprovação'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
